/**
 * 지급명령 PoC
 * - 승인된 결의 중 미지급 건 조회 · 지급확정
 */

import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import styled from 'styled-components';
import { useAuth } from 'contexts/AuthContext';
import useTenantCode, { buildPrefsUserId } from 'hooks/useTenantCode';
import { PageContainer } from 'styles/common.styles';
import { theme, SEARCH_LABEL_W } from 'styles/theme';
import {
  FormArea,
  CollapsibleSearchPanel,
  ActionButtonGroup,
  getCurrentFiscalYear,
} from 'components/forms';
import { HiGridComponent, useHiGridHook, hiCol } from 'components/grid/hiGrid';
import { showAlert, showConfirm } from 'utils/toastBridge';
import { loadPaymentCandidates, confirmPayment } from 'expense/expenseStore';

const GRID_ID = 'expense-payment-order';

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
  min-height: 0;
  height: 100%;
`;

const Panel = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 0;
  flex: 1;
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.sm};
  overflow: hidden;
  background: ${theme.colors.white};
`;

const GridBody = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
`;

const Hint = styled.div`
  font-size: ${theme.typography.fontSize.xs};
  color: ${theme.colors.text.secondary};
  padding: 0 2px;
`;

export default function ExpensePaymentOrder() {
  const { user } = useAuth();
  const tenantCode = useTenantCode();
  const prefsUserId = useMemo(() => buildPrefsUserId(tenantCode, user), [tenantCode, user]);

  const methods = useForm({
    mode: 'onSubmit',
    defaultValues: { fiscalYear: getCurrentFiscalYear() },
  });
  const { getValues, reset } = methods;

  const gridRef = useRef(null);

  const columns = useMemo(
    () => [
      hiCol.roTextColumn({ header: '결의번호', field: 'resolutionNo', size: 90, align: 'center' }),
      hiCol.roTextColumn({ header: '부서', field: 'deptName', size: 90 }),
      hiCol.roTextColumn({ header: '과목', field: 'subjectName', size: 110 }),
      hiCol.edNumberColumn({ header: '지급금액', field: 'payAmt', size: 100, align: 'right', editable: true }),
      hiCol.edDateColumn({ header: '지급일', field: 'payDate', size: 110, align: 'center', editable: true }),
      hiCol.edTextColumn({ header: '출금계좌', field: 'orgAccount', size: 120, editable: true }),
      hiCol.edTextColumn({ header: '입금계좌', field: 'vendorAccount', size: 120, editable: true }),
      hiCol.edTextColumn({ header: '입금적요', field: 'depositMemo', size: 120, editable: true }),
      hiCol.edTextColumn({ header: '출금적요', field: 'withdrawMemo', size: 120, editable: true }),
      hiCol.roTextColumn({ header: '지급', field: 'paidYn', size: 60, align: 'center' }),
      hiCol.roTextColumn({ header: 'CMS', field: 'cmsSentYn', size: 60, align: 'center' }),
      hiCol.edTextColumn({ header: '비고', field: 'remark', size: 140, editable: true }),
    ],
    []
  );

  const grid = useHiGridHook({
    gridRef,
    getSearchValues: getValues,
    autoSearch: false,
    enableContextMenu: true,
    confirmCheck: true,
  });

  const dims = useCallback(
    () => ({
      tenantCode,
      fiscalYear: String(getValues('fiscalYear') || '').trim(),
    }),
    [getValues, tenantCode]
  );

  const handleSearch = useCallback(async () => {
    const d = dims();
    if (!d.fiscalYear) {
      showAlert('회계연도를 입력하세요.');
      return;
    }
    const rows = await loadPaymentCandidates(d);
    gridRef.current?.setData?.(rows);
  }, [dims]);

  useEffect(() => {
    reset({ fiscalYear: getCurrentFiscalYear() });
  }, [reset]);

  const getSelectedResolutionUids = useCallback(() => {
    const checked = gridRef.current?.getCheckedData?.() || [];
    if (checked.length) return checked.map((r) => r.resolutionUid);
    const sel = gridRef.current?.getSelectedRow?.();
    return sel?.resolutionUid ? [sel.resolutionUid] : [];
  }, []);

  const handleConfirmPay = useCallback(
    async (withCms = false) => {
      const uids = getSelectedResolutionUids();
      if (!uids.length) {
        showAlert('지급확정할 결의를 선택(체크)하세요.');
        return;
      }
      const msg = withCms
        ? '선택 건을 지급확정하고 CMS 전송 처리할까요? (PoC: CMS는 stub)'
        : '선택 건을 지급확정할까요?';
      const ok = await showConfirm(msg);
      if (!ok) return;

      const d = dims();
      const rows = gridRef.current?.getData?.() || [];
      await confirmPayment(d, rows, uids, { cmsSent: withCms });
      await handleSearch();

      if (withCms) {
        showAlert('지급확정 및 CMS 전송 처리했습니다. (PoC stub)', 'success');
      } else {
        showAlert('지급확정했습니다.', 'success');
      }
    },
    [getSelectedResolutionUids, dims, handleSearch]
  );

  const searchFields = useMemo(
    () => [
      [
        {
          name: 'fiscalYear',
          label: '회계연도',
          type: 'fiscalYear',
          width: '88px',
          labelWidth: SEARCH_LABEL_W,
        },
      ],
    ],
    []
  );

  const actionButtons = useMemo(
    () => [
      {
        id: 'search',
        label: '조회',
        icon: '🔍',
        onClick: handleSearch,
        hotkey: 'F7',
        title: '조회 (F7)',
      },
    ],
    [handleSearch]
  );

  const extraButtons = useMemo(
    () => [
      {
        id: 'confirmPay',
        label: '지급확정',
        onClick: () => handleConfirmPay(false),
        variant: 'primary',
      },
      {
        id: 'confirmPayCms',
        label: '지급확정+CMS',
        onClick: () => handleConfirmPay(true),
        title: 'PoC: cmsSentYn=Y stub',
      },
    ],
    [handleConfirmPay]
  );

  return (
    <PageContainer>
      <FormProvider {...methods}>
        <ActionButtonGroup buttons={actionButtons} extraButtons={extraButtons} />
        <Stack>
          <CollapsibleSearchPanel
            storageKey="expense-payment-order"
            prefsUserId={prefsUserId}
            title="조회조건"
          >
            <FormArea compact fields={searchFields} />
          </CollapsibleSearchPanel>

          <Hint>승인된 결의 중 미지급 건 · 행 체크 후 지급확정</Hint>

          <Panel>
            <GridBody>
              <HiGridComponent
                ref={gridRef}
                columns={columns}
                showRowNumber
                rowNumberSize={48}
                enableColumnFilters
                enableContextMenu
                enableRowEditMenu
                enableCut
                enableFindReplace
                allowReplace
                enableHeaderMenu
                enableSelectionStats
                enableFreeze
                enableQuickExport
                exportFileName="지급명령"
                gridId={GRID_ID}
                prefsUserId={prefsUserId}
                {...(grid.contextMenuProps || {})}
              />
            </GridBody>
          </Panel>
        </Stack>
      </FormProvider>
    </PageContainer>
  );
}
